import React, { useEffect, useState } from 'react'
import { Text } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from '@chakra-ui/react'
import { ModalType } from '~components/shared/confirm-modal'
import {
    AppointmentFieldsFragment,
    RescheduleFieldsFragment,
} from '~graphql/generated/graphql'
import SchedulePreview from './schedule-preview'
import AppointmentOverview from './appointment-overview'
import { Box, Flex, HStack, useBreakpointValue } from '@chakra-ui/react'
import DenialReasonForm from '~components/shared/denial-reason-form'

interface Props {
    type: ModalType
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    onRequestChange: (isDenial: boolean, reason: string) => void
    action: string
    isLoading?: boolean
    headerOverride?: string
    helpTextOverride?: string
    appointment: AppointmentFieldsFragment
    startOnDenial?: boolean
}

const ConfirmModal: React.FC<Props> = ({
    type,
    isOpen,
    onClose,
    onConfirm,
    onRequestChange,
    isLoading,
    appointment,
    startOnDenial = false,
}) => {
    const buttonScheme = {
        [ModalType.Delete]: 'red',
        [ModalType.Confirm]: 'brand',
        [ModalType.Warning]: 'yellow',
    }

    const modalSize = useBreakpointValue({ base: 'full', md: '3xl' })
    const [isDenial, setIsDenial] = useState<boolean>(false)
    const [explanation, setExplanation] = useState<string>('')
    const [isRequestingChange, setIsRequestingChange] =
        useState<boolean>(startOnDenial)

    useEffect(() => {
        setIsRequestingChange(startOnDenial)
    }, [startOnDenial])

    useEffect(() => {
        if (!isRequestingChange) {
            setExplanation('')
        }
    }, [isRequestingChange])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={modalSize}
            scrollBehavior="inside"
        >
            <ModalOverlay>
                <ModalContent>
                    <ModalHeader borderBottomWidth="1px">
                        <Text fontWeight="medium">
                            {isRequestingChange
                                ? 'Are you sure you want to request an appointment change?'
                                : 'Are you sure you want to confirm this appointment?'}
                        </Text>
                        <Text color="gray.500" fontSize="sm">
                            {isRequestingChange
                                ? 'This will deny the current appointment.'
                                : `This action cannot be undone.`}
                        </Text>
                    </ModalHeader>
                    <ModalBody px={{ base: 0, md: 6 }} py={{ base: 0, md: 2 }}>
                        <Flex>
                            <Box
                                overflow="hidden"
                                h="full"
                                w="100%"
                                maxW={isRequestingChange ? '0px' : '100%'}
                                opacity={isRequestingChange ? 0 : 1}
                                transition="max-width 0.4s, opacity 0.2s ease-out"
                            >
                                <Box minW="min(100vw, 702px)">
                                    <Box
                                        flex={1}
                                        px={{ base: 4, md: 0 }}
                                        py={{ base: 4, md: 0 }}
                                    >
                                        <AppointmentOverview
                                            appointment={appointment}
                                        />
                                    </Box>
                                    {type === ModalType.Confirm &&
                                        appointment && (
                                            <SchedulePreview
                                                appointment={appointment}
                                            />
                                        )}
                                </Box>
                            </Box>
                            <Box
                                overflow="hidden"
                                h="full"
                                w="100%"
                                maxW={isRequestingChange ? '100%' : '0px'}
                                opacity={isRequestingChange ? 1 : 0}
                                transition="max-width 0.4s, opacity 0.2s ease-out"
                            >
                                <Box minW="min(100vw, 702px)">
                                    <Box
                                        flex={1}
                                        px={{ base: 4, md: 0 }}
                                        py={{ base: 4, md: 0 }}
                                    >
                                        <AppointmentOverview
                                            appointment={appointment}
                                            isRequestingChange={
                                                isRequestingChange
                                            }
                                        />
                                    </Box>
                                    <Box w="full" mt={8}>
                                        <DenialReasonForm
                                            client={appointment.client!}
                                            patient={appointment.patient!}
                                            explanation={explanation}
                                            setExplanation={setExplanation}
                                            isChangeRequest
                                            isDenial={isDenial}
                                            setIsDenial={setIsDenial}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Flex>
                    </ModalBody>
                    <ModalFooter
                        justifyContent="space-between"
                        borderTopWidth="1px"
                    >
                        <Button
                            onClick={() =>
                                isRequestingChange
                                    ? startOnDenial
                                        ? onClose()
                                        : setIsRequestingChange(false)
                                    : onClose()
                            }
                        >
                            {isRequestingChange || startOnDenial
                                ? 'Back'
                                : 'Cancel'}
                        </Button>
                        <HStack spacing={4}>
                            {!isRequestingChange && !startOnDenial && (
                                <Button
                                    variant="link"
                                    onClick={() => setIsRequestingChange(true)}
                                    size="sm"
                                    textDecor={'underline'}
                                    color="gray.700"
                                >
                                    Deny / Request Reschedule
                                </Button>
                            )}
                            <Button
                                colorScheme={buttonScheme[type]}
                                isLoading={isLoading}
                                onClick={() =>
                                    isRequestingChange
                                        ? onRequestChange(isDenial, explanation)
                                        : onConfirm()
                                }
                                isDisabled={isRequestingChange && !explanation}
                            >
                                {isRequestingChange
                                    ? isDenial
                                        ? 'Deny'
                                        : 'Request Change'
                                    : 'Confirm'}
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </ModalOverlay>
        </Modal>
    )
}

export default ConfirmModal
